import React from "react";
import { Navigate, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean; // true para rotas que precisam de autenticação, false para rotas públicas
}

// Função para verificar se o token é válido (básica - verifica se existe e não está vazio)
const isTokenValid = (token: string | null): boolean => {
  if (!token || token.trim() === "") {
    return false;
  }

  try {
    // Verifica se o token tem o formato JWT básico (3 partes separadas por pontos)
    const parts = token.split(".");
    if (parts.length !== 3) {
      return false;
    }

    // Decodifica o payload para verificar a expiração
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);

    // Se tem expiração e já expirou, remove o token e retorna false
    if (payload.exp && payload.exp < currentTime) {
      localStorage.removeItem("token");
      return false;
    }

    return true;
  } catch {
    // Se não conseguir decodificar, considera inválido
    localStorage.removeItem("token");
    return false;
  }
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
}) => {
  const location = useLocation();
  const token = localStorage.getItem("token");

  // Verifica se o token existe e é válido
  const isAuthenticated = isTokenValid(token);

  // Para rotas que exigem autenticação (como /home)
  if (requireAuth && !isAuthenticated) {
    // Redireciona para login se não estiver autenticado
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Para rotas públicas (como login) quando já está autenticado
  if (!requireAuth && isAuthenticated) {
    // Redireciona para home se já estiver autenticado e tentar acessar login
    return <Navigate to="/home" replace />;
  }

  // Renderiza o componente filho se as condições forem atendidas
  return <>{children}</>;
};

export default ProtectedRoute;
